import {persistentStore, activitiesStore} from '@/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {searchRequest, searchResponse} from '~src/utils/activities/activities-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {PRODUCT_NAME} from '@/utils/activities/activities-const'
import {onmessage} from '@/utils/worker-helpers'
import {newSearch as newSearchFn, partialSearch} from '@/utils/store-helpers'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'
import type {Ref} from 'vue'
import {updateWishlist as updateWishlistActivities} from '~/utils/activities/activities-helpers'
import {clean} from '~/utils/helpers'

export const useActivitiesRuntimeStore = defineStore('activitiesRuntime', () => {
    const city: Ref = ref({})
    const productRuntimeStore = useProductRuntimeStore()
    const {
        country,
        offers,
        searchActiveCount,
        partialSearchTimeoutInstance,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
        STOP_FILTER,
        stopSearch,
        filterActive,
    } = productRuntimeStore

    function SET_CITY(val: any) {
        city.value = val
        country.value = {}
    }

    function SET_COUNTRY(val: any) {
        country.value = val
        city.value = {}
    }

    function RESET() {
        searchActiveCount.value = 0
    }

    function UPDATE_WISHLIST({id, isWishlist}: any) {
        const activity = offers.value.find(offer => offer.info.id === id)
        if (!activity) return
        activity.wishlist = isWishlist
    }

    async function loadCity(id: any) {
        try {
            const {
                cities: [_city],
            } = await appInstance.$api.locations.get({id, limitCities: 1})
            city.value = _city
            country.value = {}
        } catch (e) {
            city.value = {}
        }
    }

    async function loadCountry(id: any) {
        try {
            const {
                countries: [_country],
            } = await appInstance.$api.locations.get({id, limitCountries: 1})
            country.value = _country
            city.value = {}
        } catch (e) {
            country.value = {}
        }
    }

    async function updateWishlist({id, isWishlist}: any) {
        const activity = {id}
        appInstance.$workers.activitiesWorker.postMessage({
            action: 'refreshWishlist',
            activity,
            isWishlist,
        })
        const cachedOffers = await appInstance.$localForage.getItem('activities')
        updateWishlistActivities(cachedOffers, activity, isWishlist)
        appInstance.$localForage.setItem('activities', cachedOffers)
        UPDATE_WISHLIST({id, isWishlist})
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        activitiesStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    async function reload() {
        if (city.value.id) await loadCity(city.value.id)
        if (country.value.id) await loadCountry(country.value.id)
    }

    function reset() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequest(), searchResponse(), activitiesStore)
        RESET()
        activitiesStore.RESET()
    }

    function newSearch() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequest(), searchResponse(), activitiesStore)
    }

    async function search(rq: any) {
        START_SEARCH()
        newSearchFn(stopSearch, SET_OFFERS, rq, searchResponse(), activitiesStore)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await partialSearch(
                SET_PARTIAL_SEARCH_RESULTS_CTS,
                SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
                partialSearchTimeoutInstance,
                rq,
                PRODUCT_NAME,
                activitiesStore,
                appInstance.$api.searchActivities
            )
        } finally {
            STOP_SEARCH()
        }
    }

    const searchPageLink = computed(() => (searchRequest: any) => {
        const {partialResponse, convertToCurrency, ...query} = searchRequest
        return {name: 'activities', query}
    })

    const infoPageLink = computed(() => (product: any, searchRequest: any) => {
        const {query} = searchPageLink.value(searchRequest)
        if (product.productId) query.productId = product.productId
        query.name = product.info.name
        return {name: 'activity', query}
    })

    const searchRequestFromQuery = computed(() => (query: any) => {
        //TODO Need filter $route.query params
        const rq = Object.assign(searchRequest(), query)
        if (typeof rq.childrenAges === 'string') {
            rq.childrenAges = [parseInt(rq.childrenAges, 10)]
        } else {
            rq.childrenAges = rq.childrenAges.map((age: string) => parseInt(age, 10))
        }
        rq.convertToCurrency = persistentStore.currency
        rq.adults = parseInt(rq.adults, 10)
        if (rq.cityId) rq.cityId = parseInt(rq.cityId, 10)
        rq.privateActivity = rq.privateActivity === 'true' || rq.privateActivity === true
        rq.sharedActivity = rq.sharedActivity === 'true' || rq.sharedActivity === true
        if (rq.citizenshipId) {
            rq.citizenshipId = parseInt(rq.citizenshipId, 10)
        }
        delete rq.name
        delete rq.productId
        clean(rq)

        return rq
    })

    return {
        ...productRuntimeStore,
        city,
        SET_CITY,
        SET_COUNTRY,
        RESET,
        UPDATE_WISHLIST,
        updateWishlist,
        clientInit,
        loadCity,
        changePrice,
        reload,
        reset,
        newSearch,
        search,
        searchPageLink,
        infoPageLink,
        searchRequestFromQuery,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useActivitiesRuntimeStore, import.meta.hot))
